import React, { useEffect, useMemo, useRef } from 'react';
import { GUIDE_SURFACES } from '@features/knowledge-base/links';
import BoltIcon from '@mui/icons-material/Bolt';
import DiamondIcon from '@mui/icons-material/Diamond';
import { Box, Button, type SxProps, type Theme, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { EntitlementGuideLink } from './EntitlementGuideLink';

export interface ActionButton {
  label: string;
  onClick: () => void;
  startIcon?: React.ReactNode;
}

export interface EntitlementTrackingMetadata {
  /** The capability name (e.g., "note_creation"); also selects the guide article under the actions */
  capability_name?: string;

  /** Where the overlay is being shown (e.g., "encounter_create") */
  source?: string;

  /** Whether the entitlement has a quota */
  has_quota?: boolean;

  /** Current usage amount */
  current_usage?: number;

  /** Quota limit */
  quota?: number;
}

export interface EntitlementBlockedOverlayProps {
  /** Icon to display at the top (default: DiamondIcon) */
  icon?: React.ReactNode;

  /** Main heading text */
  title: string;

  /** Description text below the title */
  description: string;

  /** Primary action button (typically "Request Upgrade") */
  primaryAction?: ActionButton;

  /** Optional secondary action button (typically "Close") */
  secondaryAction?: ActionButton;

  /** Optional tracking metadata for analytics */
  trackingMetadata?: EntitlementTrackingMetadata;

  /** Optional sx prop for the overlay container */
  sx?: SxProps<Theme>;
}

/**
 * EntitlementBlockedOverlay Component
 *
 * A reusable overlay component that blocks access to features when users
 * hit entitlement limits. Displays a centered message with call-to-action
 * buttons.
 *
 * Features:
 * - Absolute positioned overlay covering parent container
 * - Semi-transparent white backdrop with blur effect
 * - Centered content with icon, title, description, and CTAs
 * - "How {feature} works" guide link under the CTAs, chosen by
 *   `trackingMetadata.capability_name` (nothing renders without an article)
 * - Fully prop-driven for maximum reusability
 *
 * Usage:
 * ```tsx
 * <Box sx={{ position: 'relative', height: '400px' }}>
 *   <EntitlementBlockedOverlay
 *     title="You're out of credits"
 *     description="Upgrade your plan to access this feature"
 *     primaryAction={{
 *       label: 'Request Upgrade',
 *       onClick: () => handleUpgrade(),
 *       startIcon: <BoltIcon />
 *     }}
 *     secondaryAction={{
 *       label: 'Close',
 *       onClick: () => handleClose()
 *     }}
 *   />
 * </Box>
 * ```
 */
export const EntitlementBlockedOverlay: React.FC<EntitlementBlockedOverlayProps> = ({
  icon = <DiamondIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
  title,
  description,
  primaryAction,
  secondaryAction,
  trackingMetadata,
  sx,
}) => {
  const { entitlement: entitlementMetrics } = useEventTracking();
  const mountTimeRef = useRef<number>(Date.now());
  const hasTrackedShownRef = useRef(false);

  // Track overlay shown once per mount. Callers pass trackingMetadata as an
  // inline object, so its identity changes on every parent re-render.
  useEffect(() => {
    if (!trackingMetadata || hasTrackedShownRef.current) {
      return;
    }

    hasTrackedShownRef.current = true;
    entitlementMetrics.blockedOverlayShown({
      capability_name: trackingMetadata.capability_name,
      source: trackingMetadata.source,
      has_quota: trackingMetadata.has_quota,
      current_usage: trackingMetadata.current_usage,
      quota: trackingMetadata.quota,
    });
  }, [entitlementMetrics, trackingMetadata]);

  // Handle primary action with tracking
  const handlePrimaryAction = () => {
    if (trackingMetadata && primaryAction) {
      entitlementMetrics.upgradeRequested({
        capability_name: trackingMetadata.capability_name,
        source: trackingMetadata.source,
        action_label: primaryAction.label,
        has_quota: trackingMetadata.has_quota,
        current_usage: trackingMetadata.current_usage,
        quota: trackingMetadata.quota,
      });
    }

    primaryAction?.onClick();
  };

  // Handle secondary action with tracking
  const handleSecondaryAction = () => {
    if (trackingMetadata && secondaryAction) {
      const timeOnOverlay = Math.round((Date.now() - mountTimeRef.current) / 1000);

      entitlementMetrics.blockedDismissed({
        capability_name: trackingMetadata.capability_name,
        source: trackingMetadata.source,
        action_label: secondaryAction.label,
        has_quota: trackingMetadata.has_quota,
        time_on_overlay: timeOnOverlay,
      });
    }

    secondaryAction?.onClick();
  };

  const overlaySx = useMemo(() => {
    const baseStyles = {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 3,
      boxSizing: 'border-box',
    };

    if (!sx) {
      return baseStyles;
    }
    return [baseStyles, sx] as SxProps<Theme>;
  }, [sx]);

  return (
    <Box sx={overlaySx}>
      {/* Centered Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          maxWidth: 480,
          gap: 3,
        }}
      >
        {/* Icon */}
        {icon && <Box>{icon}</Box>}

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>

        {/* Actions, with the guide link in the slot under them */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {secondaryAction && (
              <Button
                variant="outlined"
                onClick={handleSecondaryAction}
                sx={{
                  minWidth: 120,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                {secondaryAction.label}
              </Button>
            )}

            {primaryAction && (
              <Button
                variant="contained"
                onClick={handlePrimaryAction}
                startIcon={primaryAction.startIcon}
                sx={{
                  minWidth: 180,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                {primaryAction.label}
              </Button>
            )}
          </Box>

          <EntitlementGuideLink
            capabilityName={trackingMetadata?.capability_name}
            surfaceId={GUIDE_SURFACES.ENTITLEMENT_OVERLAY}
          />
        </Box>
      </Box>
    </Box>
  );
};

EntitlementBlockedOverlay.displayName = 'EntitlementBlockedOverlay';

// Re-export for convenience
export { BoltIcon };
